import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Mattslag } from "~app/common/models/constants";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import * as Request from "../dto/vardeberakning/vardeberakning-request-dto";
import { VardeberakningResponseDTO } from "../dto/vardeberakning/vardeberakning-response-dto";
import { IVirkeskopMattslagRequest, IVirkeskopMattslagResponse } from "../models/atgard-models";
import { AvverkningskostnaderEvent } from "../models/avverkningskostnader/avverkningskostnader-models";
import { Kontrakt } from "../models/kontrakt-models";
import { VKForkalkylTradslagDTO } from "../models/tradslag/tradslag-models";
import { Vardeberakning } from "../models/vardeberakning/vardeberakning-models";
import { Virkeskostnader } from "../models/virkeskostnader/virkeskostnader-models";
import { AvtalService } from "./avtal.service";

@Injectable({
    providedIn: "root"
})
export class ForkalkylService {
    private readonly apiClientService = inject(ApiClientService);
    private readonly avtalService = inject(AvtalService);

    private readonly _tradslag$ = new BehaviorSubject<VKForkalkylTradslagDTO[]>([]);
    private initialized = false;

    get tradslag$() {
        this.initSubscription();
        return this._tradslag$.asObservable();
    }

    vardeberakningDefault(kontraktId: number): Observable<Vardeberakning[]> {
        return this.apiClientService.get<VardeberakningResponseDTO[]>(`virkeskop2/kontrakt/${kontraktId}/vardeberakning`).pipe(
            map(result => result.map(r => new Vardeberakning(r))));
    }

    vardeberakningForkalkyl(vardeberakningRequest: Request.VardeberakningRequestDTO): Observable<Vardeberakning> {
        return this.apiClientService.post<any, VardeberakningResponseDTO>(`virkeskop2/vardeberakning`, vardeberakningRequest)
            .pipe(map(r => new Vardeberakning(r)));
    }

    getAverageMattslag(param: IVirkeskopMattslagRequest): Observable<number> {
        if (param.mattslag === Mattslag.M3F) {
            return of(1);
        }

        return this.apiClientService.post<IVirkeskopMattslagRequest, IVirkeskopMattslagResponse>(`/Virkeskop/GetAverageMattslag`, param, true)
            .pipe(map(r => r.konverteringsfaktor));
    }

    createVardeberakningRequest(kontrakt: Kontrakt, virkeskostnader: Virkeskostnader, avverkningskostnader: AvverkningskostnaderEvent, atgardGuid?: string) {
        if (kontrakt.isLeveransvirke) {
            return this.createVardeberakningRequestFromKostnader(virkeskostnader);
        } else {
            return this.createVardeberakningRequestFromAtgarder(virkeskostnader, avverkningskostnader, atgardGuid);
        }
    }

    private createVardeberakningRequestFromKostnader(model: Virkeskostnader): Request.VardeberakningRequestDTO {
        const virkeskostnader = model.kontraktVirkeskostnader;
        const kostnadDtoer = virkeskostnader.map(m => m.mapToDto());
        const atgardDto: Request.VirkeskopsatgardDTO = {
            virkeskostnader: kostnadDtoer,
            avverkningskostnader: []
        };

        return {
            virkeskopsatgarder: [atgardDto]
        };
    }

    private createVardeberakningRequestFromAtgarder(
        virkeskostnaderModel: Virkeskostnader,
        avverkningskostnaderModel: AvverkningskostnaderEvent,
        atgardGuid?: string): Request.VardeberakningRequestDTO {
        const virkeskopsatgarder: Request.VirkeskopsatgardDTO[] = virkeskostnaderModel.atgarderVirkeskostnader
            .filter(atgard => {
                if (ObjectUtils.isNullOrUndefined(atgardGuid)) {
                    return true;
                }
                return atgard.virkeskopsatgardGUID === atgardGuid;
            })
            .map(atgard => {
                const virkeskostnader = atgard.virkeskostnader.map(m => m.mapToDto());
                const avverkningskostnadModel = avverkningskostnaderModel.getForAtgard(atgard.virkeskopsatgardGUID);
                const avverkningskostnader = avverkningskostnadModel?.mapToDto() ?? [];
                return {
                    virkeskostnader,
                    avverkningskostnader
                };
            });

        return {
            virkeskopsatgarder
        };
    }

    private initSubscription() {
        if (this.initialized) {
            return;
        }

        this.avtalService.getAllForkalkylTradslag()
            .subscribe(tradslag => this._tradslag$.next(tradslag));
        this.initialized = true;
    }
}
